import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ClickSpark from '../components/ClickSpark';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Github, Linkedin, Mail, MapPin, Calendar, Users, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';

const Team = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });

  // Leadership Team
  const leadershipTeam = [
    {
      id: "anand-lo",
      name: "Anand Lo",
      role: "President",
      image: `${process.env.PUBLIC_URL}/AnandLo_Anand Lo.jpeg`,
      year: "Applied Computer Science",
      bio: "Applied Computer Science student with a minor in Statistics, focusing on ML applications in oceanography and biosecurity.",
      skills: ["Foundation models", "Retrieval systems", "Spatiotemporal modeling", "Multimodal representation learning"],
      social: {
        linkedin: "https://www.linkedin.com/in/anandlo/",
        github: "sparkle",
        email: "anandlo@dal.ca"
      }
    },
    {
      id: "het-jivani",
      name: "Het Jivani",
      role: "Internal Vice President",
      image: `${process.env.PUBLIC_URL}/Het_Jivani_digital_photo.jpeg`,
      year: "Computer Science",
      bio: "Managing internal operations, member engagement, and ensuring smooth coordination of society activities and initiatives.",
      skills: ["Operations", "Member Relations", "Event Coordination", "Project Management"],
      social: {
        linkedin: "#",
        github: "#",
        email: "internal.vp@dmls.ca"
      }
    },
    {
      id: "kai-patel",
      name: "Kai Patriquin Patel",
      role: "Vice President (External)",
      image: `${process.env.PUBLIC_URL}/KP.png`,
      year: "Computer Science",
      bio: "A highly driven individual committed to continuous learning and growth in machine learning.",
      skills: ["Python", "Machine Learning", "Problem Solving", "Leadership"],
      social: {
        linkedin: "https://www.linkedin.com/in/kai-patel-035537210",
        github: "kp42",
        email: "kaipatel32310@gmail.com"
      }
    },
    {
      id: "rafat-hossain",
      name: "Rafat Hossain",
      role: "Treasurer",
      image: `${process.env.PUBLIC_URL}/Rafat.jpg`,
      year: "Computer Science",
      bio: "Treasurer for DMLS, managing financial operations. Volunteering as an ML Researcher on a project focused on understanding glacier melting and its links to climate change.",
      skills: ["Photography", "Full-stack Web Dev", "Python", "JavaScript", "Java", "Data Analysis"],
      social: {
        linkedin: "https://www.linkedin.com/in/rafat--hossain/",
        github: "dirter.rh",
        email: "mh836316@dal.ca"
      }
    }
  ];

  // Operational Team
  const operationalTeam = [
    {
      id: "shuvro-pal",
      name: "Shuvro Pal",
      role: "Events coordinator",
      image: `${process.env.PUBLIC_URL}/headshot_Shuvro Pal.png`,
      year: "Computer Science",
      department: "Research",
      bio: "PhD student focusing on intelligent and autonomous systems, combining autonomous vehicles, wireless communication, reinforcement learning, and generative AI.",
      social: {
        linkedin: "https://www.linkedin.com/in/shuvropal/",
        github: "shuvro_pal",
        email: "shuvro@dal.ca"
      }
    },
    {
      id: "nafisah-nubah",
      name: "Nafisah Nubah",
      role: "Lead of Events and Social Media",
      image: `${process.env.PUBLIC_URL}/headshot-nubah_Nafisah Nubah.jpg`,
      year: "Computer Science",
      department: "Communications",
      bio: "3rd-year Computer Science student passionate about exploring ML applications and community engagement.",
      social: {
        linkedin: "https://www.linkedin.com/in/nafisah-nubah-3a355829b/",
        github: "nubah123",
        email: "Nafisah.Nubah@dal.ca"
      }
    },
    {
      id: "chizulu-zephaniah",
      name: "Chizulu Zephaniah",
      role: "Web Developer",
      image: `${process.env.PUBLIC_URL}/Chizulu.jpg`,
      year: "Computer Science",
      department: "Technology & Infrastructure",
      bio: "Building and maintaining DMLS digital platforms, websites, and technical infrastructure for the society.",
      social: {
        linkedin: "https://www.linkedin.com/in/chizulu-zephaniah-906582308/",
        github: "zephwys",
        email: "zephchizulu@gmail.com"
      }
    },
    {
      id: "akash-maity",
      name: "Akash Maity",
      role: "Student Representative",
      image: `${process.env.PUBLIC_URL}/Akash.jpg`,
      year: "Computer Science",
      department: "Student Relations",
      bio: "Third-year Computer Science student with a strong interest in AI, automation, and software development. Bridging communication between students and leadership team while helping organize engaging AI-focused events.",
      social: {
        linkedin: "https://www.linkedin.com/in/akash-maity-8888a22b3",
        github: "mike000522",
        email: "ak298902@dal.ca"
      }
    },
    {
      id: "sophie-eruokwu",
      name: "Sophie Eruokwu",
      role: "Mentorship Lead",
      image: `${process.env.PUBLIC_URL}/Sophie.jpg`,
      year: "Computer Science",
      department: "Mentorship & Community",
      bio: "Third-year Computer Science student focusing on AI, Cybersecurity, and FinTech. Passionate about supporting the community and turning everyday problems into practical coded solutions.",
      social: {
        linkedin: "https://www.linkedin.com/in/sophie-e-b66a1a228/",
        github: "chrissy9730",
        email: "sp332391@dal.ca"
      }
    }
  ];

  const stats = [
    { icon: Users, number: "9", label: "Team Members" },
    { icon: Calendar, number: "1", label: "Year Established" },
    { icon: Award, number: "5+", label: "Events Organized" },
    { icon: MapPin, number: "100+", label: "Students Reached" }
  ];

  const navigate = useNavigate();

  // Delayed navigation handler for team member cards
  const handleMemberClick = (e, memberId) => {
    e.preventDefault();
    navigate(`/team/${memberId}`);
  };

  return (
    <ClickSpark sparkColor="#000" sparkSize={12} sparkRadius={24} sparkCount={10} duration={500}>
      <div className="min-h-screen bg-white">
        <Navbar />
        {/* Hero Section */}
        <section ref={heroRef} className="pt-16 sm:pt-24 pb-8 sm:pb-16 bg-cover bg-center bg-no-repeat relative overflow-hidden" style={{ backgroundImage: 'url(/6.jpg)' }}>
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        
        {/* Enhanced colorful background elements */}
        <motion.div 
          className="absolute top-20 right-20 w-32 h-32 bg-[#0395fc]/20 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-20 w-48 h-48 bg-yellow-300/15 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.div 
          className="absolute top-1/3 left-1/4 w-24 h-24 bg-[#4ea8f4]/25 rounded-full blur-xl"
          animate={{ 
            scale: [0.8, 1.4, 0.8],
            opacity: [0.25, 0.55, 0.25]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div 
          className="absolute bottom-1/3 right-1/3 w-36 h-36 bg-[#0993f8]/18 rounded-full blur-2xl"
          animate={{ 
            scale: [1.1, 0.9, 1.1],
            opacity: [0.2, 0.45, 0.2]
          }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        />
        <motion.div 
          className="absolute top-2/3 right-1/4 w-20 h-20 bg-yellow-400/20 rounded-full blur-lg"
          animate={{ 
            scale: [0.9, 1.3, 0.9],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
        <motion.div 
          className="absolute top-1/4 left-1/2 w-28 h-28 bg-[#0070c0]/22 rounded-full blur-xl"
          animate={{ 
            scale: [1.2, 0.8, 1.2],
            opacity: [0.25, 0.5, 0.25]
          }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
        />
        
        {/* Geometric shapes with color */}
        <motion.div 
          className="absolute top-1/6 right-1/6 w-16 h-16 bg-[#0395fc]/30 rotate-45 blur-lg"
          animate={{ 
            rotate: [45, 135, 45],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-yellow-300/25 rounded-sm blur-md"
          animate={{ 
            rotate: [0, 90, 0],
            opacity: [0.25, 0.55, 0.25]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
        <motion.div 
          className="absolute top-3/4 right-1/2 w-18 h-18 bg-[#525253]/15 rounded-full blur-xl"
          animate={{ 
            scale: [1, 1.5, 1],
            opacity: [0.15, 0.4, 0.15]
          }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
        />
        <motion.div 
          className="absolute bottom-1/6 left-2/3 w-14 h-14 bg-[#0395fc]/20 rotate-12 blur-lg"
          animate={{ 
            rotate: [12, 102, 12],
            opacity: [0.28, 0.58, 0.28]
          }}
          transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
        />

        <div className="container mx-auto px-4 max-w-sm sm:max-w-2xl lg:max-w-4xl xl:max-w-6xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full border border-white/30 text-sm font-bold mb-8 backdrop-blur-sm"
            >
              <Users className="size-4" />
              MEET OUR TEAM
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 sm:mb-6 leading-tight"
            >
              The Minds Behind
              <br />
              <span className="text-yellow-300">DMLS</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-base sm:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto mb-8 sm:mb-12 px-4"
            >
             Discover the students powering DMLS and contributing to the growth of machine learning at Dalhousie University.
      
            </motion.p>

            {/* Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-4xl mx-auto px-4"
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    whileHover={{ scale: 1.1 }}
                    className="text-center"
                  >
                    <Icon className="size-6 sm:size-8 mx-auto mb-2 sm:mb-4 text-yellow-300" />
                    <div className="text-xl sm:text-2xl lg:text-3xl font-black mb-1 sm:mb-2">{stat.number}</div>
                    <div className="text-xs sm:text-sm text-white/80 uppercase tracking-wider">{stat.label}</div>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#0395fc]/10 text-[#0395fc] px-4 py-2 rounded-full border-2 border-[#0395fc] text-sm font-bold mb-6">
              <Award className="size-4" />
               Click on a team member to view their full profile.
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 sm:mb-6">
              Executive Team
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
              Our executive team leads DMLS with vision and dedication, driving our society forward.
            </p>
          </motion.div>

          {/* Executive Members */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16">
            {leadershipTeam.map((member, index) => (
              <a
                key={index}
                href={`/team/${member.id}`}
                className="h-full"
                onClick={e => handleMemberClick(e, member.id)}
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group cursor-pointer h-full flex flex-col"
                >
                  {/* Profile Avatar with Initials */}
                  <div className="relative h-48 flex flex-col items-center justify-center overflow-hidden">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/logo192.png`; // Fallback image
                      }}
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-[#0395fc]/8 group-hover:bg-[#0070c0]/25 transition-colors duration-300"></div>
                    {/* Social Links */}
                    <div className="relative z-10 flex gap-3 mt-auto mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {member.social.linkedin && (
                        <motion.a 
                          href={member.social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                        >
                          <Linkedin className="size-4 text-white hover:text-[#0395fc]" />
                        </motion.a>
                      )}
                      {member.social.email && (
                        <motion.a 
                          href={`mailto:${member.social.email}`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                        >
                          <Mail className="size-4 text-white hover:text-[#0395fc]" />
                        </motion.a>
                      )}
                    </div>
                  </div>
                  {/* Member Info */}
                  <div className="p-4 flex-1 flex flex-col min-h-[200px]">
                    <div>
                      <h3 className="text-lg font-black text-foreground mb-1">{member.name}</h3>
                      <p className="text-[#0395fc] font-bold text-sm mb-1">{member.role}</p>
                      <p className="text-xs text-muted-foreground mb-3">{member.year}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3 leading-relaxed line-clamp-3">{member.bio}</p>
                    {/* Skills */}
                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {member.skills.slice(0, 3).map((skill, skillIndex) => (
                        <span 
                          key={skillIndex}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full border"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </a>
            ))}
          </div>

          {/* Operational Team Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-32 mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-4 sm:mb-6">
              Operational Team
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
              The dedicated team driving our initiatives, events, and daily operations.
            </p>
          </motion.div>

          {/* Operational Team Members */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {operationalTeam.map((member, index) => (
              <Link key={index} to={`/team/${member.id}`} className="h-full">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group cursor-pointer h-full flex flex-col"
                >
                <div className="relative h-48 flex flex-col items-center justify-center overflow-hidden">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${process.env.PUBLIC_URL}/logo192.png`; // Fallback image
                    }}
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-[#0395fc]/8 group-hover:bg-[#0070c0]/25 transition-colors duration-300"></div>
                  
                  {/* Social Links */}
                  <div className="relative z-10 flex gap-3 mt-auto mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {member.social.linkedin && (
                      <motion.a 
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                      >
                        <Linkedin className="size-4 text-white hover:text-[#0395fc]" />
                      </motion.a>
                    )}
                    {member.id === 'chizulu-zephaniah' && member.social.github && (
                      <motion.a 
                        href={member.social.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                      >
                        <Github className="size-4 text-white hover:text-[#0395fc]" />
                      </motion.a>
                    )}
                    {member.social.email && (
                      <motion.a 
                        href={`mailto:${member.social.email}`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                      >
                        <Mail className="size-4 text-white hover:text-[#0395fc]" />
                      </motion.a>
                    )}
                  </div>
                </div>

                {/* Member Info */}
                <div className="p-4 flex-1 flex flex-col min-h-[200px]">
                  <div>
                    <h3 className="text-lg font-black text-foreground mb-1">{member.name}</h3>
                    <p className="text-[#0395fc] font-bold text-sm mb-1">{member.role}</p>
                    <p className="text-xs text-muted-foreground mb-3">{member.year}</p>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-3 leading-relaxed line-clamp-3">{member.bio}</p>
                  
                  {/* Department */}
                  <div className="flex flex-wrap gap-1.5 mt-auto">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full border">
                      {member.department}
                    </span>
                  </div>
                </div>
              </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-24 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 sm:mb-6">
              Want to Join Our Team?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              We're always looking for passionate individuals to join our mission. Whether you're interested in leadership, research, or community building.
            </p>
            <a
              href="https://airtable.com/appbzvXorTGCwSsy9/pagtWdxjYq0FJQosY/form"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#0395fc] hover:bg-[#0395fc]/90 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold border-2 sm:border-4 border-[#0395fc] shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200"
              >
                Get Involved
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>

        <Footer2 />
      </div>
    </ClickSpark>
  );
};

export default Team;